import { getDataStreamType } from 'dashboard-engine/util/getDataStreamType';
import { useAvailableDataStreamDefinitionsForWorkspace } from 'queries/hooks/useAvailableDataStreamDefinitionsForWorkspace';
import { EditorSteps } from '../constants';
import { useTileEditorStepsContext } from '../contexts/TileEditorStepsContext';
import { TileEditorDataStream } from '../state/TileEditorState';
import { useTileEditorStore } from '../state/TileEditorStoreProvider';
import { TileEditorDataStreams } from '../state/useTileEditorDataStreams';
import { useScopeBaseQuery } from './useDataStreamObjectFilters';
import { useObjectFilterObjects } from './objectFilters/useObjectFilterObjects';

export const useSplashScreenDataStreamSelect = (
    dataSourceId: string,
    dataStreams: NonNullable<ReturnType<typeof useAvailableDataStreamDefinitionsForWorkspace>['data']>,
    tileEditorDataStreams: TileEditorDataStreams
) => {
    const { setCurrentEditorStep } = useTileEditorStepsContext();
    const { dispatch } = useTileEditorStore((s) => ({ dispatch: s.dispatch }));

    let dataStreamInstance: TileEditorDataStream | undefined;

    const datastreamsForDataSource = dataStreams.filter(({ pluginId }) => pluginId === dataSourceId);

    if (datastreamsForDataSource.length === 1) {
        dataStreamInstance = tileEditorDataStreams.dataStreamsById.get(datastreamsForDataSource[0].id);
    }

    /**
     * We want to know if there is exactly 1 object matching the selected data stream,
     * so we need to load the objects without any filters applied.
     * Some filters are carried over to the Objects step from the data stream step,
     * so it's possible for some to be set without the user having visited the Objects step.
     */
    const { scopeBaseQuery, isLoading: isLoadingScopeBaseQuery } = useScopeBaseQuery(dataStreamInstance, undefined);
    
    const { objects } = useObjectFilterObjects({
        scopeBaseQuery,
        queryParams: {},
        isFilterQueryReady: !isLoadingScopeBaseQuery && dataStreamInstance != null,
        options: { keepPreviousData: false }
    });

    return {
        selectDatasource: () => {
            if (dataStreamInstance) {
                const {
                    supportsScope,
                    isConfigurable
                } = getDataStreamType(dataStreamInstance);
                    
                // Matches behaviour and logic in SplashScreenDataStreamOption.tsx, be sure to keep them in sync!
                if (supportsScope && objects && objects?.length === 1) {
                    dispatch({ 
                        type: 'dataStream.selectDataStream', 
                        dataStream: dataStreamInstance
                    });
                    return setCurrentEditorStep(isConfigurable ? EditorSteps.parameters : EditorSteps.timeframe, true);
                } 
                
                if (supportsScope) {
                    dispatch({ 
                        type: 'dataStream.selectDataStream', 
                        dataStream: dataStreamInstance,
                        forceStep: EditorSteps.objects
                    });
                    return setCurrentEditorStep(EditorSteps.objects, true);
                }
            
                if (isConfigurable) {
                    dispatch({ 
                        type: 'dataStream.selectDataStream', 
                        dataStream: dataStreamInstance,
                        forceStep: EditorSteps.parameters
                    });
                    return setCurrentEditorStep(EditorSteps.parameters, true);
                }
            
                dispatch({ 
                    type: 'dataStream.selectDataStream', 
                    dataStream: dataStreamInstance,
                    forceStep: EditorSteps.timeframe
                });
                return setCurrentEditorStep(EditorSteps.timeframe, true);
            }
        
            dispatch({ type: 'dataStream.updateFilters', filters: { dataSourceId } });
            setCurrentEditorStep(EditorSteps.dataStream);
        }
    };
};
